import { useState, useEffect } from 'react';
import useModalScrollLock from '../hooks/useModalScrollLock';
import { useRouter } from 'next/router';
import { useSession, signIn } from 'next-auth/react';
import TapSurface from './TapSurface';

const challenges = [
  {
    id: 1,
    name: "Starter Challenge",
    description: "Perfect for beginners looking to get started",
    startingBalance: 5000,
    target: 1000,
    maxBet: 250,
    badge: "BEGINNER"
  },
  {
    id: 2,
    name: "Pro Challenge",
    description: "For experienced bettors ready to scale up",
    startingBalance: 10000,
    target: 2000,
    maxBet: 500,
    badge: "POPULAR"
  },
  {
    id: 3,
    name: "Elite Challenge",
    description: "Maximum stakes for serious professionals",
    startingBalance: 25000,
    target: 5000,
    maxBet: 1250,
    badge: "ADVANCED"
  }
];

export default function DemoPopup({ isOpen, onClose, initialIndex = 1 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userSplit, setUserSplit] = useState(70);
  const [showRules, setShowRules] = useState(false);
  const [step, setStep] = useState('selection');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingTrial, setExistingTrial] = useState(null);
  const router = useRouter();
  const { data: session, update: updateSession } = useSession();

  useModalScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setStep('selection');
      setAuthEmail('');
      setAuthPassword('');
      setConfirmPassword('');
      setAuthError('');
    }
  }, [isOpen, initialIndex]);

  const handleChallengeSelect = (index) => {
    setCurrentIndex(index);
    setShowDropdown(false);
  };

  const handleContinue = () => {
    if (session) {
      startDemo();
    } else {
      setStep('auth');
    }
  };

  const startDemo = () => {
    const demoChallenge = {
      ...currentChallenge,
      userSplit,
      isDemoMode: true,
      startedAt: new Date().toISOString()
    };
    localStorage.setItem('demo_challenge', JSON.stringify(demoChallenge));
    onClose();
    router.push('/demo-dashboard');
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');

    if (!authEmail.trim()) {
      setAuthError('Please enter an email address');
      setLoading(false);
      return;
    }

    if (authPassword.length < 6) {
      setAuthError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (isSignUp && authPassword !== confirmPassword) {
      setAuthError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        const signupRes = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: authEmail.trim(), password: authPassword })
        });
        const signupData = await signupRes.json();
        if (!signupRes.ok) throw new Error(signupData.error || 'Signup failed');
      }

      const result = await signIn('credentials', {
        redirect: false,
        email: authEmail.trim(),
        password: authPassword
      });

      if (result?.error) {
        throw new Error('Invalid email or password');
      }

      await updateSession();
      
      // Check if user has an existing demo trial
      const existingChallenge = localStorage.getItem('demo_challenge');
      const existingState = localStorage.getItem('demo_state');
      if (existingChallenge && existingState) {
        setExistingTrial(JSON.parse(existingChallenge));
        setStep('existing_trial');
      } else {
        startDemo();
      }
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'auth') {
      setStep('selection');
      setAuthError('');
    }
  };

  if (!isOpen) return null;

  const currentChallenge = challenges[currentIndex];

  const getThemeColors = () => {
    if (currentChallenge.badge === 'BEGINNER') {
      return {
        primary: 'blue',
        border: 'border-blue-500',
        borderColor: '#3b82f6',
        borderLight: 'border-blue-400/50',
        shadow: 'shadow-blue-400/20',
        bg: 'bg-blue-500/20',
        text: 'text-blue-400',
        gradient: 'from-blue-500 to-blue-600',
        gradientHover: 'hover:from-blue-600 hover:to-blue-700',
        splitGradient: 'from-blue-500/10 to-blue-600/10',
        splitBorder: 'border-blue-500/30',
        splitBar: 'from-blue-400 to-blue-500'
      };
    } else if (currentChallenge.badge === 'POPULAR') {
      return {
        primary: 'green',
        border: 'border-green-500',
        borderColor: '#22c55e',
        borderLight: 'border-green-400/50',
        shadow: 'shadow-green-400/20',
        bg: 'bg-green-500/20',
        text: 'text-green-400',
        gradient: 'from-green-500 to-blue-500',
        gradientHover: 'hover:from-green-600 hover:to-blue-600',
        splitGradient: 'from-green-500/10 to-blue-500/10',
        splitBorder: 'border-green-500/30',
        splitBar: 'from-green-400 to-green-500'
      };
    } else {
      return {
        primary: 'purple',
        border: 'border-purple-500',
        borderColor: '#a855f7',
        borderLight: 'border-purple-400/50',
        shadow: 'shadow-purple-400/20',
        bg: 'bg-purple-500/20',
        text: 'text-purple-400',
        gradient: 'from-purple-500 to-purple-600',
        gradientHover: 'hover:from-purple-600 hover:to-purple-700',
        splitGradient: 'from-purple-500/10 to-purple-600/10',
        splitBorder: 'border-purple-500/30',
        splitBar: 'from-purple-400 to-purple-500'
      };
    }
  };

  const theme = getThemeColors();

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div 
        className="popup-content relative bg-black rounded-3xl max-w-md w-full my-auto border-2"
        style={{ 
          borderColor: theme.borderColor,
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 bg-[#111] hover:bg-[#1a1a1a] rounded-full flex items-center justify-center"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {step === 'auth' && (
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 z-20 w-8 h-8 bg-[#111] hover:bg-[#1a1a1a] rounded-full flex items-center justify-center"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {step === 'existing_trial' && existingTrial ? (
          <div className="p-6 pt-12">
            <div className="text-center mb-6">
              <div className="mb-4">
                <img src="/pikslogotransparent.png" alt="Piks Logo" className="h-32 mx-auto" style={{ filter: 'none' }} />
              </div>
              <div className="inline-flex items-center bg-green-500/20 text-green-400 px-3 py-1.5 rounded-full text-xs font-bold border border-green-500/30 mb-3">
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                EXISTING TRIAL FOUND
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Continue Your Trial?</h3>
              <p className="text-gray-400 text-sm">
                You have an active demo trial in progress
              </p>
            </div>

            <div className="bg-[#111] rounded-xl border border-[#1a1a1a] p-4 mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-400">Challenge:</span>
                <span className="text-white font-medium">{existingTrial.name}</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-400">Starting Balance:</span>
                <span className="text-green-400 font-medium">${existingTrial.startingBalance?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Your Split:</span>
                <span className="text-blue-400 font-medium">{existingTrial.userSplit}%</span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  onClose();
                  router.push('/demo-dashboard');
                }}
                className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300"
              >
                Continue Trial
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('demo_challenge');
                  localStorage.removeItem('demo_state');
                  localStorage.removeItem('demo_bet_history');
                  setExistingTrial(null);
                  setStep('selection');
                }}
                className="w-full bg-[#1a1a1a] hover:bg-[#222] text-white font-bold py-3 px-6 rounded-xl transition-all duration-300"
              >
                Start New Trial
              </button>
            </div>
          </div>
        ) : step === 'auth' ? (
          <div className="p-6 pt-12">
            <div className="text-center mb-6">
              <div className="mb-4">
                <img src="/pikslogotransparent.png" alt="Piks Logo" className="h-32 mx-auto" style={{ filter: 'none' }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {isSignUp ? 'Create Account' : 'Sign In'}
              </h3>
              <p className="text-gray-400 text-sm">
                {isSignUp ? 'Create an account to start your demo' : 'Sign in to continue to your demo'}
              </p>
            </div>

            {authError && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-sm text-center">{authError}</p>
              </div>
            )}

            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full px-4 py-3 pr-12 bg-[#1a1a1a] border-2 border-[#1a1a1a] rounded-xl text-white placeholder-gray-400 focus:outline-none transition-all auth-input"
                    style={{ '--focus-color': theme.borderColor }}
                    placeholder="Enter your email"
                    required
                  />
                  {authEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail) && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-20 bg-[#1a1a1a] border-2 border-[#1a1a1a] rounded-xl text-white placeholder-gray-400 focus:outline-none transition-all auth-input"
                    style={{ '--focus-color': theme.borderColor }}
                    placeholder="Enter your password"
                    minLength="6"
                    required
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                    {authPassword.length >= 6 && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-300"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {isSignUp && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                  <div className="relative">
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 bg-[#1a1a1a] border-2 border-[#1a1a1a] rounded-xl text-white placeholder-gray-400 focus:outline-none transition-all auth-input"
                      style={{ '--focus-color': theme.borderColor }}
                      placeholder="Confirm your password"
                      minLength="6"
                      required
                    />
                    {confirmPassword.length >= 6 && confirmPassword === authPassword && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${theme.gradient} ${theme.gradientHover} disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 disabled:cursor-not-allowed`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
                  </div>
                ) : (
                  isSignUp ? 'Create Account & Continue' : 'Sign In & Continue'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setAuthError('');
                  setAuthPassword('');
                  setConfirmPassword('');
                }}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>

            <div className="mt-4 p-3 bg-[#111] rounded-xl border border-[#1a1a1a]">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Selected:</span>
                <span className={`${theme.text} font-medium`}>{currentChallenge.name}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-400">Your Split:</span>
                <span className="text-green-400 font-medium">{userSplit}%</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-400">Price:</span>
                <span className="text-amber-400 font-bold">FREE</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 pt-8">
            <div className="text-center mb-4">
              <div className="mb-4">
                <img src="/pikslogotransparent.png" alt="Piks Logo" className="h-32 mx-auto" style={{ filter: 'none' }} />
              </div>
            </div>

            <div className="text-center mb-4">
              <div className="inline-flex items-center bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-xs font-bold border border-amber-500/30 mb-3">
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                </svg>
                FREE DEMO MODE
              </div>
              <p className="text-gray-400 text-xs">Practice with virtual funds - No payment required</p>
            </div>

            <div className="space-y-3 mb-6" style={{ WebkitTapHighlightColor: 'transparent' }}>
              <div className="relative">
                <div
                  onClick={() => setShowDropdown(!showDropdown)}
                  className={`flex justify-between items-center py-3 px-4 bg-[#111] rounded-xl border-2 ${theme.borderLight} cursor-pointer hover:${theme.border} transition-all duration-300 shadow-lg ${theme.shadow}`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div>
                    <span className="text-gray-300 font-medium text-sm">Starting Balance</span>
                    <div className={`text-xs ${theme.text} font-medium`}>Click to change</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`${theme.text} font-bold`}>${currentChallenge.startingBalance.toLocaleString()}</span>
                    <svg className={`w-4 h-4 ${theme.text} transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {showDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-[#0d0d0d] border border-[#1a1a1a] rounded-xl shadow-2xl z-20" style={{ WebkitTapHighlightColor: 'transparent' }}>
                    {challenges.map((challenge, index) => (
                      <div
                        key={challenge.id}
                        onClick={() => handleChallengeSelect(index)}
                        className={`flex justify-between items-center py-3 px-4 cursor-pointer hover:bg-[#1a1a1a] transition-all duration-200 ${
                          index === currentIndex ? `${theme.bg} border-l-4 ${theme.border}` : ''
                        } ${index === 0 ? 'rounded-t-xl' : ''} ${index === challenges.length - 1 ? 'rounded-b-xl' : ''}`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div>
                          <span className="text-white font-medium text-sm">{challenge.name}</span>
                          <div className="text-xs text-gray-400">{challenge.badge}</div>
                        </div>
                        <span className={`${theme.text} font-bold`}>${challenge.startingBalance.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-[#111] rounded-xl border border-[#1a1a1a] p-4" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowRules(!showRules)}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <h4 className="text-white font-semibold text-sm">Challenge Rules</h4>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showRules ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {showRules && (
                  <div className="space-y-1 text-xs mt-2 pb-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Pick Minimum</span>
                      <span className="text-white font-medium">20 picks</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Risk Range</span>
                      <span className="text-white font-medium">1% - 5%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Max Daily Loss</span>
                      <span className="text-white font-medium">10%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Max Drawdown</span>
                      <span className="text-white font-medium">15%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Profit Target (Phase 1 & 2)</span>
                      <span className="text-green-400 font-medium">20%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Pick Cashout Fee</span>
                      <span className="text-white font-medium">10%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Reward Split (After Phase 2)</span>
                      <span className="text-blue-400 font-medium">90%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Same Game Parlays</span>
                      <span className="text-green-400 font-medium">YES</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!showRules && (
              <div className={`p-4 bg-gradient-to-r ${theme.splitGradient} rounded-2xl border ${theme.splitBorder} mb-4 relative`} style={{ WebkitTapHighlightColor: 'transparent' }}>
                <button
                  onClick={() => setUserSplit(70)}
                  className="absolute top-2 right-2 w-6 h-6 bg-[#1a1a1a]/80 hover:bg-[#222] rounded-full flex items-center justify-center transition-colors"
                  title="Reset to 70%"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                <div className="text-center mb-3">
                  <div className="text-sm font-medium text-gray-300">Split Boost</div>
                  <div className="text-xs text-gray-400">Drag anywhere on the bar to boost your split</div>
                </div>

                <div
                  className="flex h-10 rounded-xl overflow-hidden border border-[#1a1a1a] cursor-grab active:cursor-grabbing relative"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onMouseDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const startX = e.clientX;
                    const startSplit = userSplit;

                    const handleMouseMove = (e) => {
                      const deltaX = e.clientX - startX;
                      const deltaPercent = (deltaX / rect.width) * 100;
                      const newSplit = Math.max(70, Math.min(90, startSplit + deltaPercent));
                      setUserSplit(Math.round(newSplit));
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                  onTouchStart={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const startX = e.touches[0].clientX;
                    const startSplit = userSplit;

                    const handleTouchMove = (e) => {
                      const deltaX = e.touches[0].clientX - startX;
                      const deltaPercent = (deltaX / rect.width) * 100;
                      const newSplit = Math.max(70, Math.min(90, startSplit + deltaPercent));
                      setUserSplit(Math.round(newSplit));
                    };

                    const handleTouchEnd = () => {
                      document.removeEventListener('touchmove', handleTouchMove);
                      document.removeEventListener('touchend', handleTouchEnd);
                    };

                    document.addEventListener('touchmove', handleTouchMove);
                    document.addEventListener('touchend', handleTouchEnd);
                  }}
                >
                  <div
                    className={`bg-gradient-to-r ${theme.splitBar} flex items-center justify-center text-white text-xs font-bold transition-all duration-150`}
                    style={{ width: `${userSplit}%` }}
                  >
                    You {userSplit}%
                  </div>
                  <div
                    className="bg-gradient-to-r from-slate-600 to-slate-700 flex items-center justify-center text-white text-xs font-bold transition-all duration-150"
                    style={{ width: `${100 - userSplit}%` }}
                  >
                    Us {100 - userSplit}%
                  </div>
                </div>
              </div>
            )}

            {!showRules && (
              <div className="text-center mb-4 p-3 bg-[#111] rounded-xl border border-[#1a1a1a]" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <div className="flex items-center justify-center space-x-2">
                  <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">FREE</div>
                </div>
                <div className="text-gray-400 text-xs">
                  Demo experience - practice mode
                </div>
              </div>
            )}

            <TapSurface
              onTap={handleContinue}
              isActive={true}
              activeColor="#f59e0b"
              className="w-full font-bold py-3 px-6 rounded-xl mb-4 text-center"
            >
              Start Demo Challenge
            </TapSurface>

            <div className="flex justify-center space-x-2 mb-4" style={{ WebkitTapHighlightColor: 'transparent' }}>
              {challenges.map((_, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors cursor-pointer`}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    backgroundColor: index === currentIndex ? theme.borderColor : '#4b5563'
                  }}
                />
              ))}
            </div>

            <div className="text-center pt-2 border-t border-[#1a1a1a]">
              <p className="text-gray-500 text-xs mb-2">
                This is a simulated experience with virtual funds.
              </p>
              <button
                onClick={() => {
                  onClose();
                  window.dispatchEvent(new CustomEvent('openChallengePopup'));
                }}
                className={`${theme.text} hover:opacity-80 text-sm font-medium transition-colors`}
              >
                Ready for the real thing? Get Funded →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
